// Client-side events and tours. The API is the source of truth for created events, sign-ups and results (ADR 0013);
// the browser keeps a copy of my stage results and entries for display while the network is down.
// The game's own events (library and calendar) are computed here, without network.
import {
  EVENT_LIBRARY, calendar, findBuiltinEvent, normalizeEvent, standings,
  type EventDef, type StageResult, type TourStandings, type CatId, type TimelinePoint,
} from '@gruvetto/core';
import type { EventSummary, EventEntry, AiEventResponse, EventVisibility } from '@gruvetto/contracts';
import { api, currentUser, ApiError } from './api.js';

const KEY = 'gv-events-v1';
interface LocalStore { results: Record<string, StageResult[]>; entries: Record<string, CatId> }

const load = (): LocalStore => {
  try { return { results: {}, entries: {}, ...JSON.parse(localStorage.getItem(KEY) || '{}') }; } catch { return { results: {}, entries: {} }; }
};
const save = (s: LocalStore) => { try { localStorage.setItem(KEY, JSON.stringify(s)); } catch { /* quota or private mode */ } };
/** Sign-out: the next player must not see my results. */
export const clearEventCache = () => { try { localStorage.removeItem(KEY); } catch { /* ignore */ } };

/** Leaderboard key of the local player: their account. */
export const myKey = (): string => currentUser()?.id ?? 'local';

export interface EventView extends Omit<EventSummary, 'def'> { def: EventDef }
const builtin = (def: EventDef, myCat: string | null = null): EventView => ({ def, owner: null, visibility: 'builtin', entries: 0, myCat, mine: false });
const fromApi = (s: EventSummary): EventView => ({ ...s, def: normalizeEvent(s.def).def });
const offline = (e: unknown) => e instanceof ApiError && e.offline;

export interface EventLists { calendar: EventView[]; library: EventView[]; mine: EventView[]; community: EventView[]; joined: EventView[]; offline?: boolean }

export async function listEvents(now = Date.now()): Promise<EventLists> {
  const local = load();
  const cal = calendar(now, 7).map(d => builtin(d, local.entries[d.id] ?? null));
  const lib = EVENT_LIBRARY.map(d => builtin(d, local.entries[d.id] ?? null));
  try {
    const r = await api<{ mine: EventSummary[]; public: EventSummary[]; joined: EventSummary[] }>('/events');
    const joined = r.joined.map(fromApi), cats = new Map(joined.map(j => [j.def.id, j.myCat]));
    const withCat = (v: EventView) => ({ ...v, myCat: cats.get(v.def.id) ?? v.myCat });
    return { calendar: cal.map(withCat), library: lib.map(withCat), mine: r.mine.map(fromApi), community: r.public.map(fromApi), joined: joined.filter(j => j.visibility !== 'builtin' && !j.mine) };
  } catch (e) {
    if (!offline(e)) throw e;
    return { calendar: cal, library: lib, mine: [], community: [], joined: [], offline: true };
  }
}

export interface EventDetail extends EventView { roster: EventEntry[] }

export async function getEvent(id: string): Promise<EventDetail | null> {
  const local = load();
  const b = findBuiltinEvent(id);
  try {
    const r = await api<EventSummary & { entries: EventEntry[] }>(`/events/${encodeURIComponent(id)}`);
    return { ...fromApi(r), entries: r.entries.length, roster: r.entries };
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return null;
    if (!offline(e)) throw e;
    return b ? { ...builtin(b, local.entries[id] ?? null), roster: [] } : null;
  }
}

/** Saves (creates or edits) one of my events. Returns the bounded definition and the corrections. */
export async function saveEvent(raw: EventDef, visibility: EventVisibility): Promise<{ def: EventDef; warnings: string[] }> {
  const { def } = normalizeEvent(raw);
  if (findBuiltinEvent(def.id)) def.id = 'ev-' + Date.now().toString(36);
  const r = await api<EventSummary & { warnings: string[] }>(`/events/${encodeURIComponent(def.id)}`, { method: 'PUT', body: { def, visibility } });
  return { def: normalizeEvent(r.def).def, warnings: r.warnings };
}

export async function deleteEvent(id: string) {
  await api(`/events/${encodeURIComponent(id)}`, { method: 'DELETE' });
  const s = load(); delete s.results[id]; delete s.entries[id]; save(s);
}

export async function enter(id: string, cat: CatId) {
  await api(`/events/${encodeURIComponent(id)}/entry`, { method: 'POST', body: { cat } });
  const s = load(); s.entries[id] = cat; save(s);
}
export async function withdraw(id: string) {
  await api(`/events/${encodeURIComponent(id)}/entry`, { method: 'DELETE' });
  const s = load(); delete s.entries[id]; save(s);
}
export const localEntry = (id: string): CatId | null => load().entries[id] ?? null;

/** `timelines`: the tour between players after each stage, per category, as the server tells it (null while offline). */
export interface EventResults { mine: StageResult[]; personal: TourStandings; players: TourStandings | null; timelines: Record<string, TimelinePoint[]> | null; roster: EventEntry[]; offline?: boolean }

/** My stages (my own standings) and the standings between players. */
export async function eventResults(def: EventDef): Promise<EventResults> {
  const local = load().results[def.id] || [];
  try {
    const r = await api<{ mine: StageResult[]; players: TourStandings; timelines?: Record<string, TimelinePoint[]>; entries: EventEntry[] }>(`/events/${encodeURIComponent(def.id)}/results`);
    const mine = mergeResults(r.mine, local.filter(l => !r.mine.some(m => m.stage === l.stage)));
    return { mine, personal: standings(def, mine), players: r.players, timelines: r.timelines ?? {}, roster: r.entries };
  } catch (e) { if (!offline(e)) throw e; return { mine: local, personal: standings(def, local), players: null, timelines: null, roster: [], offline: true }; }
}
const mergeResults = (a: StageResult[], b: StageResult[]) => [...a, ...b].sort((x, y) => x.stage - y.stage);

/** Keeps a stage result (a local copy, then the server). Returns false if the upload failed. */
export async function saveStageResult(def: EventDef, res: StageResult, activityId: string | null, receipt: string): Promise<{ ok: boolean; error?: string }> {
  const s = load(), list = (s.results[def.id] || []).filter(r => r.stage !== res.stage);
  s.results[def.id] = [...list, res].sort((a, b) => a.stage - b.stage);
  if (!s.entries[def.id]) s.entries[def.id] = (res.riders.find(r => r.key === myKey())?.cat as CatId) || 'E';
  save(s);
  try { await api(`/events/${encodeURIComponent(def.id)}/results/${res.stage}`, { method: 'PUT', body: { activityId, receipt } }); return { ok: true }; } catch (e) { return { ok: false, error: (e as Error).message }; }
}

/** Describes or tweaks an event in natural language through the server (Claude, or its keyword generator). */
export async function generateEvent(prompt: string, current: EventDef | null): Promise<{ def: EventDef; warnings: string[]; source: 'claude' | 'local'; note?: string }> {
  const r = await api<AiEventResponse>('/ai/event', { method: 'POST', body: { prompt, current, tzOffset: new Date().getTimezoneOffset() } });
  return { ...r, def: normalizeEvent(r.def).def };
}
